import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Stack, Typography,
  TextField, MenuItem, Button, IconButton, Tooltip, Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import {
  AILength, AITone, IAIGenerateResult, aiGenerateTemplateBody,
} from "../../../Api/emailTemplatesApi";

const NAVY = "#0F172A";
const CHAMPAGNE = "#C9A961";
const CHAMPAGNE_PALE = "#F5EFE0";
const HAIRLINE = "rgba(10,14,26,0.06)";

const TONES: { value: AITone; label: string; hint: string }[] = [
  { value: "Professional",  label: "Professional",  hint: "Polished business voice" },
  { value: "Friendly",      label: "Friendly",      hint: "Warm and conversational" },
  { value: "Luxury",        label: "Luxury / Premium", hint: "Elevated, hospitality-grade" },
  { value: "Formal",        label: "Formal",        hint: "Traditional and reserved" },
  { value: "Warm",          label: "Warm",          hint: "Personable and reassuring" },
  { value: "Corporate",     label: "Corporate",     hint: "B2B and account-focused" },
  { value: "Sales-focused", label: "Sales-focused", hint: "Persuasive with clear CTA" },
];

const LENGTHS: { value: AILength; label: string; hint: string }[] = [
  { value: "Short",    label: "Short",    hint: "~2 short paragraphs" },
  { value: "Medium",   label: "Medium",   hint: "~3 balanced paragraphs" },
  { value: "Detailed", label: "Detailed", hint: "~4–5 specifics-rich paragraphs" },
];

const PURPOSE_PRESETS = [
  "Follow-up after quote",
  "Thank customer for booking",
  "Booking reminder",
  "Payment reminder",
  "Event reminder",
  "Quote reminder",
  "Corporate inquiry response",
  "Welcome new lead",
];

interface Props {
  /** Pre-fill values inherited from the editor form. */
  category?: string | null;
  categoryLabel?: string | null;
  name?: string;
  subject?: string;
  /** Whether the body editor already has content — drives the
   *  "Replace existing body?" confirmation step on apply. */
  hasExistingBody: boolean;
  onClose: () => void;
  onApply: (result: IAIGenerateResult) => void;
}

const AIGenerateDialog: React.FC<Props> = ({
  category, categoryLabel, name, subject, hasExistingBody, onClose, onApply,
}) => {
  const [tone, setTone]       = useState<AITone>("Professional");
  const [length, setLength]   = useState<AILength>("Medium");
  const [purpose, setPurpose] = useState<string>("");
  const [generating, setGenerating] = useState(false);
  const [error, setError]   = useState<string | null>(null);
  const [result, setResult] = useState<IAIGenerateResult | null>(null);

  const onGenerate = async () => {
    setError(null);
    setGenerating(true);
    try {
      const out = await aiGenerateTemplateBody({
        category: category ?? undefined,
        category_label: categoryLabel ?? undefined,
        name,
        subject,
        tone,
        length,
        purpose: purpose.trim() || undefined,
      });
      setResult(out);
    } catch (e: any) {
      setError(e?.message || "Generation failed.");
    }
    setGenerating(false);
  };

  const onConfirmApply = () => {
    if (!result) return;
    if (hasExistingBody) {
      const ok = window.confirm("Replace the current body with the generated content?");
      if (!ok) return;
    }
    onApply(result);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" gap={1}>
            <AutoAwesomeIcon sx={{ fontSize: 20, color: CHAMPAGNE }} />
            <Box>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: NAVY }}>
                Generate Body with AI
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                Drafts professional content using the template category, subject, and your tone choices.
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2.5 }}>
        {/* ── Echo the context the AI will be given ─────────────── */}
        <Box sx={{
          p: 1.5, mb: 2, borderRadius: 1.2,
          bgcolor: CHAMPAGNE_PALE,
          border: `1px solid ${HAIRLINE}`,
        }}>
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#A8893E", letterSpacing: "0.08em", mb: 0.5 }}>
            CONTEXT
          </Typography>
          <Stack gap={0.3}>
            <Typography sx={{ fontSize: 12 }}>
              <strong>Category:</strong> {categoryLabel || category || "—"}
            </Typography>
            <Typography sx={{ fontSize: 12 }}>
              <strong>Template:</strong> {name || <em style={{ color: "#94a3b8" }}>untitled</em>}
            </Typography>
            <Typography sx={{ fontSize: 12 }}>
              <strong>Subject:</strong> {subject || <em style={{ color: "#94a3b8" }}>—</em>}
            </Typography>
          </Stack>
        </Box>

        <Stack gap={1.5}>
          <Stack direction={{ xs: "column", sm: "row" }} gap={1.2}>
            <TextField
              select size="small" fullWidth label="Tone"
              value={tone} onChange={(e) => setTone(e.target.value as AITone)}
            >
              {TONES.map(t => (
                <MenuItem key={t.value} value={t.value}>
                  <Stack>
                    <span>{t.label}</span>
                    <Typography sx={{ fontSize: 10.5, color: "#94a3b8" }}>{t.hint}</Typography>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select size="small" fullWidth label="Length"
              value={length} onChange={(e) => setLength(e.target.value as AILength)}
            >
              {LENGTHS.map(l => (
                <MenuItem key={l.value} value={l.value}>
                  <Stack>
                    <span>{l.label}</span>
                    <Typography sx={{ fontSize: 10.5, color: "#94a3b8" }}>{l.hint}</Typography>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <TextField
            size="small" fullWidth multiline minRows={2}
            label="Purpose (optional)"
            placeholder="e.g. Follow-up two weeks after quote with a soft nudge"
            value={purpose} onChange={(e) => setPurpose(e.target.value)}
            helperText="Adds extra direction the AI couldn't infer from the category and subject alone."
          />

          <Box>
            <Typography sx={{ fontSize: 11, color: "#64748b", mb: 0.6 }}>
              Quick purpose presets — click to insert:
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5} useFlexGap>
              {PURPOSE_PRESETS.map(p => (
                <Tooltip key={p} title="Use as purpose" arrow>
                  <Box
                    role="button"
                    onClick={() => setPurpose(p)}
                    sx={{
                      px: 1, py: 0.3, borderRadius: "999px",
                      bgcolor: "#fff", border: `1px solid ${HAIRLINE}`,
                      fontSize: 10.5, fontWeight: 600, color: "#475569",
                      cursor: "pointer",
                      "&:hover": { borderColor: CHAMPAGNE, bgcolor: "#fff7e8" },
                    }}
                  >
                    {p}
                  </Box>
                </Tooltip>
              ))}
            </Stack>
          </Box>

          {error && <Alert severity="error" sx={{ fontWeight: 600 }}>{error}</Alert>}

          {/* ── Result preview ───────────────────────────────── */}
          {result && (
            <Box sx={{
              border: `1px solid ${HAIRLINE}`, borderRadius: 1.2,
              bgcolor: "#fafbfc", p: 2, mt: 0.5,
            }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.08em" }}>
                  GENERATED BODY
                </Typography>
                <Typography sx={{ fontSize: 10.5, color: "#94a3b8" }}>
                  {result.variables.length} variable{result.variables.length === 1 ? "" : "s"} detected
                </Typography>
              </Stack>
              <Box
                sx={{
                  fontSize: 13.5, color: "#1e293b", lineHeight: 1.55,
                  "& p": { mt: 0, mb: 1 },
                  maxHeight: 320, overflow: "auto",
                }}
                dangerouslySetInnerHTML={{ __html: result.body }}
              />
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, justifyContent: "space-between" }}>
        <Box>
          {result && (
            <Button onClick={onGenerate} disabled={generating} sx={{ textTransform: "none" }}>
              Regenerate
            </Button>
          )}
        </Box>
        <Stack direction="row" gap={1}>
          <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
          {result ? (
            <Button
              onClick={onConfirmApply}
              variant="contained"
              sx={{
                textTransform: "none", fontWeight: 700,
                bgcolor: NAVY, "&:hover": { bgcolor: "#1e293b" },
              }}
            >
              Insert into Editor
            </Button>
          ) : (
            <Button
              onClick={onGenerate}
              disabled={generating}
              variant="contained"
              startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: "none", fontWeight: 700,
                bgcolor: NAVY, "&:hover": { bgcolor: "#1e293b" },
              }}
            >
              {generating ? "Generating…" : "Generate"}
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default AIGenerateDialog;
